import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { RollApplication } from "../models/RollApplication";
import {
	removeRollApplication,
	upsertRollApplication,
} from "../repositories/RollApplicationsRepository";
import { ROLL_APPLICATIONS_QUERY_KEYS } from "./useRollApplications";

interface MutationOptions {
	onSuccess?: (data?: RollApplication) => void;
	onError?: (error: Error) => void;
}

export const useUpsertRollApplication = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (app: RollApplication) => {
			return await upsertRollApplication(app);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ROLL_APPLICATIONS_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertRollApplication: mutation.mutate,
		upsertRollApplicationAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteRollApplication = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeRollApplication(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ROLL_APPLICATIONS_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteRollApplication: mutation.mutate,
		deleteRollApplicationAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
